import { AdBreak, PlaybackMode, PlaybackPolicyHandler } from '@yospace/admanagement-sdk';

/**
 * Playback policy.
 */
export class DefaultPlaybackPolicy extends PlaybackPolicyHandler {
  private playbackMode: any;

  /**
   * Constructor for playback policy.
   */
  constructor() {
    super();
    this.playbackMode = null;
  }

  /**
   * Tells if the volume can be changed.
   * @param {boolean} mute The current mute state.
   * @param {number} playhead The current playhead position.
   * @param {Array} timeline The timeline.
   * @returns {boolean}
   */
  canChangeVolume(mute: boolean, playhead: number, timeline: any[]): boolean {
    return true;
  }

  /**
   * Tells if can be clicked trough.
   * @param {string} url The url.
   * @param {number} playhead The current playhead position.
   * @param {Array} timeline The timeline.
   * @returns {boolean}
   */
  canClickThrough(url: string, playhead: number, timeline: Array<any>): boolean {
    return true;
  }

  /**
   * Tells if it can pause.
   * @param {number} playhead  The current playhead position.
   * @param {Array} timeline  The timeline.
   * @returns {boolean}
   */
  canPause(playhead: number, timeline: Array<any>): boolean {
    return this.playbackMode !== PlaybackMode.LIVE;
  }

  /**
   * Tells if can resize.
   * @param {boolean} fullscreen  The current fullscreen state.
   * @param {number} playhead The current playhead position.
   * @param {Array} timeline The timeline.
   * @returns {boolean}
   */
  canResize(fullscreen: boolean, playhead: number, timeline: Array<any>): boolean {
    return true;
  }

  /**
   * Tells if the creative can be resized.
   * @param {boolean} expand The current expand state.
   * @param {number} playhead  The current playhead position.
   * @param {Array} timeline The timeline.
   * @returns {boolean}
   */
  canResizeCreative(expand: boolean, playhead: number, timeline: Array<any>): boolean {
    return true;
  }

  /**
   * Tells if the possible to skip.
   * @param {number} playhead The current playhead position.
   * @param {Array} timeline The timeline.
   * @param {number} duration The duration.
   * @returns {number}
   */
  canSkip(playhead: number, timeline: Array<any>, duration: number): number {
    if (this.playbackMode !== this.playbackMode.LIVE) {
      // We can never skip live adverts
      return -1;
    }

    const ad = this.isInAdvert(playhead, timeline);
    if (!ad) {
      return -1;
    }

    let skipOffset = ad.getSkipOffset();
    if (skipOffset === -1) {
      // We can only skip if the advert allows it
      return -1;
    }

    if (!ad.isActive()) {
      // We can skip an inactive advert
      skipOffset = 0;
    } else if (skipOffset >= 0) {
      // Calculate the difference between offset and playhead, unless negative
      skipOffset = Math.max(0, ad.getStart() + skipOffset - playhead);
    }

    // Finally, don't skip if we are in NLSO playback and any offset would put the playhead beyond the maximum position
    if (this.playbackMode === this.playbackMode.NLSO) {
      if (ad.getStart() + ad.getDuration() - playhead > duration) {
        skipOffset = -1;
      }
    }

    return skipOffset;
  }

  /**
   * Tells if it possible to stop.
   * @param {number} playhead The current playhead position.
   * @param {Array} timeline  The timeline.
   * @returns {boolean}
   */
  canStop(playhead: number, timeline: Array<any>): boolean {
    return true;
  }

  /**
   * Returns the closest ad break Prior to the given playhead position.
   * @param {number} position The playhead position.
   * @param {Array} timeline  The timeline.
   * @returns {AdBreak}
   */
  closestActiveBreakPriorTo(position: number, timeline: Array<any>): AdBreak {
    let closest = null;
    for (let i = 0; i < timeline.length; i += 1) {
      const br = timeline[i];
      if (br.getStart() + br.getDuration() < position && br.isActive()) {
        closest = br;
      }
    }

    return closest;
  }

  /**
   * Disable all adbreaks we sought past.
   * @param {number} from The start position.
   * @param {number} to The end position.
   * @param {Array} timeline The timeline.
   */
  didSeek(from: number, to: number, timeline: Array<any>) {
    // If we sought forward in a VOD/NLSO stream, disable all adbreaks we sought past
    if (this.playbackMode !== this.playbackMode.LIVE && from < to) {
      this.setInactiveAllAdBreaksBetween(from, to, timeline);
    }
  }

  /**
   * Returns true if a given ad break is active.
   * @param {number} playhead  The current playhead position.
   * @param {Array} timeline  The timeline.
   */
  isInActiveBreak(playhead: number, timeline: Array<any>): AdBreak | null {
    for (let i = 0; i < timeline.length; i += 1) {
      const br = timeline[i];
      if (br.getStart() <= playhead && playhead < br.getStart() + br.getDuration()) {
        return br.isActive() ? br : null;
      }
    }

    return null;
  }

  /**
   * Returns true if a given ad advert is active.
   * @param {number} playhead  The current playhead position.
   * @param {Array} timeline  The timeline.
   */
  isInAdvert(playhead: number, timeline: Array<any>) {
    for (let i = 0; i < timeline.length; i += 1) {
      const br = timeline[i];
      if (br.getStart() <= playhead && playhead < br.getStart() + br.getDuration()) {
        for (let j = 0; j < br.getAdverts.length; j += 1) {
          const ad = br.getAdverts()[j];
          if (ad.getStart() <= playhead && playhead < ad.getStart() + ad.getDuration()) {
            return ad;
          }
        }
      }
    }

    return null;
  }

  /**
   * Tells if an ad were skipped.
   * @param {number} from  The start position.
   * @param {number} to The end position.
   * @param {Array} timeline  The timeline.
   */
  didSkip(from: number, to: number, timeline: Array<any>) {
    return this.didSeek(from, to, timeline);
  }

  /**
   * Sets all adbreaks between the given positions to inactive.
   * @param {number} start  The start position.
   * @param {number} end  The end position.
   * @param {Array} timeline The timeline.
   */
  setInactiveAllAdBreaksBetween(start: number, end: number, timeline: Array<any>) {
    for (let i = 0; i < timeline.length; i += 1) {
      const br = timeline[i];
      if (br.isActive() && br.getStart() >= start && br.getStart() + br.getDuration() <= end) {
        br.setInactive();
      }
    }
  }

  /**
   * Sets the current playback mode.
   * @param {PlaybackMode} mode The playback mode.
   */
  setPlaybackMode(mode: PlaybackMode) {
    this.playbackMode = mode;
  }

  /**
   * Returns to the client the playhead position that the user can seek to.
   * @param {number} position  The current playhead position.
   * @param {Array} timeline The timeline.
   */
  willSeekTo(position: number, timeline: Array<any>) {
    let br = this.isInActiveBreak(position, timeline);
    let actual = position;
    if (br) {
      // If we're in an ad break, return from the break start
      actual = br.getStart();
    } else {
      // If not, find the closest break prior to the playhead
      br = this.closestActiveBreakPriorTo(position, timeline);
      if (br) {
        // If there is one, return the closest active break start.
        // If not, return the posuition pased in (i.e., we won't seek).
        actual = br.getStart();
      }
    }

    return actual;
  }
}
